import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import io from 'socket.io-client';
import { BACKEND_URL, GOOGLE_MAPS_API_KEY } from '../config';
import './PublicMapView.css';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

// Center on India by default
const center = {
  lat: 20.5937,
  lng: 78.9629
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
};

const PublicMapView = ({ onLoginClick }) => {
  const [boats, setBoats] = useState([]);
  const [socket, setSocket] = useState(null);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const [currentBoatId, setCurrentBoatId] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [watchPositionId, setWatchPositionId] = useState(null);
  const [map, setMap] = useState(null);
  const [showBoatList, setShowBoatList] = useState(true);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const onLoad = useCallback(map => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Check if we're on a mobile device
  useEffect(() => {
    const mobileDetected = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobileDetected);
    
    // If on mobile, automatically show mobile controls option
    if (mobileDetected) {
      setShowMobileControls(true);
    }
  }, []);

  // Initialize socket connection with reconnection
  useEffect(() => {
    console.log('Initializing socket connection to:', BACKEND_URL);
    const newSocket = io(BACKEND_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    newSocket.on('connect', () => {
      console.log('Socket connected successfully');
      setError(null);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setError('Connection error. Trying to reconnect...');
    });

    setSocket(newSocket);

    // Clean up on unmount
    return () => {
      if (newSocket) {
        console.log('Disconnecting socket');
        newSocket.disconnect();
      }
      
      if (watchPositionId !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchPositionId);
      }
    };
  }, [watchPositionId]);

  // Listen for real-time updates with better error handling
  useEffect(() => {
    if (!socket) return;

    const handleLocationUpdate = (newLocation) => {
      console.log('Received location update:', newLocation);
      
      setBoats(prev => {
        const newBoats = [...prev];
        const boatIndex = newBoats.findIndex(b => b.boatId === newLocation.boatId);
        
        if (boatIndex >= 0) {
          newBoats[boatIndex] = {
            ...newBoats[boatIndex],
            lastLocation: {
              ...newLocation,
              timestamp: new Date()
            }
          };
        } else {
          newBoats.push({
            boatId: newLocation.boatId,
            lastLocation: {
              ...newLocation,
              timestamp: new Date()
            }
          });
        }
        
        return newBoats;
      });
    };

    socket.on('locationUpdate', handleLocationUpdate);

    return () => {
      socket.off('locationUpdate', handleLocationUpdate);
    };
  }, [socket]);

  // Fetch boats with retry mechanism
  const fetchBoats = async () => {
    setLoading(true);
    try {
      console.log('Fetching boats from:', `${BACKEND_URL}/api/boats`);
      const response = await fetch(`${BACKEND_URL}/api/boats`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch boats: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Boats received:', data);
      
      // Filter out boats without location data
      const validBoats = data.filter(boat => boat && boat.lastLocation);
      setBoats(validBoats);
      setError(null);
    } catch (err) {
      console.error('Error fetching boats:', err);
      setError(`Unable to load boat data. Please try again later.`);
      
      // Retry after 5 seconds
      setTimeout(() => {
        fetchBoats();
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  // Fetch boats on mount and when refresh is triggered
  useEffect(() => {
    fetchBoats();

    // Set up auto-refresh if enabled
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(fetchBoats, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [lastRefreshTime, autoRefresh]);

  const refreshData = () => {
    setLastRefreshTime(Date.now());
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const toggleBoatList = () => {
    setShowBoatList(!showBoatList);
  };

  // Start GPS tracking
  const startTracking = () => {
    if (!currentBoatId) {
      alert('Please enter a boat ID');
      return;
    }
    
    setIsTracking(true);
    
    // Start watching position
    if (navigator.geolocation) {
      const id = navigator.geolocation.watchPosition(
        position => {
          const locationData = {
            boatId: currentBoatId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            speed: position.coords.speed || 0,
            heading: position.coords.heading || 0
          };
          
          // Send location to server
          if (socket) {
            console.log('Sending location update:', locationData);
            socket.emit('updateLocation', locationData);
            
            // Also update the local boats array to show our own position immediately
            setBoats(prev => {
              const newBoats = [...prev];
              const boatIndex = newBoats.findIndex(b => b.boatId === locationData.boatId);
              
              if (boatIndex >= 0) {
                newBoats[boatIndex].lastLocation = {
                  ...locationData,
                  timestamp: new Date()
                };
              } else {
                newBoats.push({ 
                  boatId: locationData.boatId, 
                  lastLocation: {
                    ...locationData,
                    timestamp: new Date()
                  }
                });
              }
              
              return newBoats;
            });
          }
        },
        error => {
          console.error('Error getting location:', error);
          alert('Error tracking location. Please check your GPS permissions.');
          setIsTracking(false);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000
        }
      );
      
      // Save the watch ID for cleanup
      setWatchPositionId(id);
    } else {
      alert('Geolocation is not supported by this browser.');
      setIsTracking(false);
    }
  };

  const stopTracking = () => {
    setIsTracking(false);
    
    // Clear geolocation watch
    if (watchPositionId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchPositionId);
      setWatchPositionId(null);
    }
  };

  // Get an approximation of the map center based on available boats
  const getMapCenter = () => {
    if (boats.length === 0) return center;
    
    // If there's a selected boat, center on it
    if (selectedBoat) {
      const boat = boats.find(b => b.boatId === selectedBoat);
      if (boat && boat.lastLocation) {
        return {
          lat: boat.lastLocation.latitude,
          lng: boat.lastLocation.longitude
        };
      }
    }
    
    // Otherwise, find the average position of all boats
    let totalLat = 0;
    let totalLng = 0;
    let count = 0;
    
    boats.forEach(boat => {
      if (boat.lastLocation) {
        totalLat += boat.lastLocation.latitude;
        totalLng += boat.lastLocation.longitude;
        count++;
      }
    });
    
    if (count > 0) {
      return {
        lat: totalLat / count,
        lng: totalLng / count
      };
    }
    
    return center;
  };

  // Format the timestamp into a readable date/time
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Calculate how long ago the location was updated
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const now = new Date();
    const updateTime = new Date(timestamp);
    const diffMs = now - updateTime;
    
    // Convert to minutes
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    
    // Convert to hours
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    // Convert to days
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days ago`;
  };

  // Helper function to get boat display name
  const getBoatDisplayName = (boatId) => {
    const boat = boats.find(b => b.boatId === boatId);
    return boat ? (boat.displayName || boat.boatId) : boatId;
  };

  // Render mobile tracking controls
  const renderMobileControls = () => {
    if (!showMobileControls) return null;
    
    return (
      <div className="mobile-controls-overlay">
        {isTracking ? (
          <div className="tracking-view">
            <div className="status-message">
              <p>आप अभी स्थान भेज रहे हैं: <strong>{getBoatDisplayName(currentBoatId)}</strong></p>
              <p>Sharing location as: <strong>{getBoatDisplayName(currentBoatId)}</strong></p>
            </div>
            <button className="stop-btn" onClick={stopTracking}>रुकें | Stop</button>
          </div>
        ) : (
          <div className="start-tracking-view">
            <h3>अपनी स्थिति साझा करें | Share Your Location</h3>
            <p>नाव की पहचान के लिए एक ID दर्ज करें | Enter an ID to identify your boat</p>
            <input
              type="text"
              value={currentBoatId}
              onChange={(e) => setCurrentBoatId(e.target.value)}
              placeholder="नाव ID | Boat ID"
              className="boat-id-input"
            />
            <button 
              className="start-btn"
              onClick={startTracking}
              disabled={!currentBoatId}
            >
              शुरू करें | Start Tracking
            </button>
            <button 
              className="cancel-btn"
              onClick={() => setShowMobileControls(false)}
            >
              रद्द करें | Cancel
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loadError) {
    return <div className="map-error">Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div className="map-loading">Loading maps...</div>;
  }

  return (
    <div className="public-map-view">
      <div className="map-header">
        <h1>नाव ट्रैकर | Boat Tracker</h1>
        <div className="map-controls">
          <button onClick={refreshData} className="refresh-btn">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button 
            onClick={toggleAutoRefresh} 
            className={`auto-refresh-btn ${autoRefresh ? 'active' : ''}`}
          >
            Auto Refresh {autoRefresh ? 'On' : 'Off'}
          </button>
          <button onClick={toggleBoatList} className="toggle-list-btn">
            {showBoatList ? 'Hide List' : 'Show List'}
          </button>
          {isMobile && !showMobileControls && (
            <button 
              onClick={() => setShowMobileControls(true)} 
              className="share-location-btn"
            >
              GPS साझा करें | Share GPS
            </button>
          )}
          <button onClick={onLoginClick} className="login-btn">Admin/Driver Login</button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="map-container">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={getMapCenter()}
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions}
        >
          {boats.map(boat => {
            if (!boat.lastLocation) return null;
            
            const position = {
              lat: boat.lastLocation.latitude,
              lng: boat.lastLocation.longitude
            };
            
            return (
              <Marker
                key={boat.boatId}
                position={position}
                icon={{
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 6,
                  fillColor: selectedBoat && selectedBoat.boatId === boat.boatId ? '#1E88E5' : '#757575',
                  fillOpacity: 1,
                  strokeColor: '#FFF',
                  strokeWeight: 1,
                  rotation: boat.lastLocation.heading || 0
                }}
                onClick={() => setSelectedBoat(boat)}
              />
            );
          })}
          
          {selectedBoat && selectedBoat.lastLocation && (
            <InfoWindow
              position={{
                lat: selectedBoat.lastLocation.latitude,
                lng: selectedBoat.lastLocation.longitude
              }}
              onCloseClick={() => setSelectedBoat(null)}
            >
              <div className="info-window">
                <h3>{selectedBoat.displayName || selectedBoat.boatId}</h3>
                <p>
                  <strong>Speed:</strong> {selectedBoat.lastLocation.speed} km/h<br />
                  <strong>Heading:</strong> {selectedBoat.lastLocation.heading}°<br />
                  <strong>Last update:</strong> {new Date(selectedBoat.lastLocation.timestamp).toLocaleString()}
                </p>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {showBoatList && (
          <div className="boat-list">
            <h3>Active Boats ({boats.length})</h3>
            <div className="boat-items">
              {boats.length === 0 && !loading ? (
                <div className="no-boats">No boats are currently active.</div>
              ) : (
                boats.map(boat => (
                  <div 
                    key={boat.boatId} 
                    className={`boat-item ${selectedBoat && selectedBoat.boatId === boat.boatId ? 'selected' : ''}`}
                    onClick={() => setSelectedBoat(boat)}
                  >
                    <div className="boat-name">{boat.displayName || boat.boatId}</div>
                    <div className="last-update">
                      Updated: {getTimeAgo(boat.lastLocation?.timestamp)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {isMobile && showMobileControls && (
        <div className="mobile-controls">
          <input
            type="text"
            value={currentBoatId}
            onChange={(e) => setCurrentBoatId(e.target.value)}
            placeholder="Enter Boat ID"
            className="boat-id-input"
          />
          {!isTracking ? (
            <button onClick={startTracking} className="start-tracking-btn">
              Start Tracking
            </button>
          ) : (
            <button onClick={stopTracking} className="stop-tracking-btn">
              Stop Tracking
            </button>
          )}
        </div>
      )}
      
      {renderMobileControls()}
    </div>
  );
};

export default PublicMapView; 